import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  signal,
  inject,
  computed,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { interval, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { IconComponentV2 } from '../../../../../../shared/components/icon-component/icon.component';
import { PaginationComponent } from '../../../../../../shared/components/pagination/pagination.component';

import {
  ScheduleAgentService,
  AgentSchedule,
  ScheduleRunLog,
  DagNodeExecution,
  PaginationInfo,
  DeliveryConfig,
  DeliveryChannelType,
} from '../../../../../../core/services/schedule-agent.service';

type ViewTab = 'schedules' | 'logs' | 'dag' | 'delivery';
type DetailsTab = 'input' | 'output' | 'tools';

@Component({
  selector: 'app-schedule',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponentV2, PaginationComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './schedule.component.html',
})
export class ScheduleComponent implements OnInit, OnChanges {
  private svc = inject(ScheduleAgentService);

  @Input() botId: number | null = null;
  private lastFetchedId: number | null = null;

  // Tab state
  activeTab = signal<ViewTab>('schedules');

  // Data
  schedules = signal<AgentSchedule[]>([]);
  runLogs = signal<ScheduleRunLog[]>([]);
  dagExecutions = signal<DagNodeExecution[]>([]);
  selectedExecutionId = signal<string | null>(null);
  selectedLogForDag = signal<ScheduleRunLog | null>(null);

  // Pagination
  readonly logsPageSize = 10;
  logsOffset = signal(0);
  logsTotal = signal(0);
  logsPagination = signal<PaginationInfo | null>(null);
  logsTotalTokensUsed = signal(0);
  logsPageTokensUsed = signal(0);

  // Loading states
  schedulesLoading = signal(false);
  logsLoading = signal(false);
  dagLoading = signal(false);
  togglingId = signal<number | null>(null);

  // Expanded agents list tracking
  expandedLogIds = signal<number[]>([]);

  // Modal logic for payloads
  detailsModalOpen = signal(false);
  modalTitle = signal('');
  modalContent = signal<any>(null);
  modalType = signal<'text' | 'json'>('text');
  detailsTab = signal<DetailsTab>('output');

  // Auto-refresh
  private pollSub?: Subscription;
  lastRefreshed = signal<Date | null>(null);

  // ── Delivery Settings ─────────────────────────────────────────────────────
  deliveryConfig = signal<DeliveryConfig | null>(null);
  deliveryLoading = signal(false);
  deliverySaving = signal(false);
  deliveryTesting = signal(false);

  emailEnabled = signal(false);
  emailRecipients = signal('');

  googleChatEnabled = signal(false);
  googleChatWebhookUrl = signal('');

  private deliveryBaseline = signal<{
    emailEnabled: boolean;
    emailRecipients: string;
    googleChatEnabled: boolean;
    googleChatWebhookUrl: string;
  } | null>(null);

  deliveryDirty = computed(() => {
    const base = this.deliveryBaseline();
    if (!base) return false;
    return (
      base.emailEnabled !== this.emailEnabled() ||
      base.emailRecipients !== this.normalizeRecipients(this.emailRecipients()) ||
      base.googleChatEnabled !== this.googleChatEnabled() ||
      base.googleChatWebhookUrl !== this.normalizeWebhook(this.googleChatWebhookUrl())
    );
  });

  // Pagination computed
  currentPage  = computed(() => this.logsPagination()?.currentPage ?? Math.floor(this.logsOffset() / this.logsPageSize) + 1);
  totalPages   = computed(() => this.logsPagination()?.totalPages ?? Math.max(1, Math.ceil(this.logsTotal() / this.logsPageSize)));
  hasPrevPage  = computed(() => this.logsPagination()?.hasPrev ?? this.logsOffset() > 0);
  hasNextPage  = computed(() => this.logsPagination()?.hasNext ?? this.logsOffset() + this.logsPageSize < this.logsTotal());
  logsEndIndex = computed(() => Math.min(this.logsOffset() + this.logsPageSize, this.logsTotal()));

  // Computed stats
  activeSchedulesCount = computed(() => this.schedules().filter((s) => s.isActive).length);
  totalRunsToday = computed(() => {
    const today = new Date().toDateString();
    return this.runLogs().filter((l) => new Date(l.firedAt).toDateString() === today).length;
  });
  successRateToday = computed(() => {
    const today = new Date().toDateString();
    const todayLogs = this.runLogs().filter((l) => new Date(l.firedAt).toDateString() === today);
    if (!todayLogs.length) return null;
    const successes = todayLogs.filter((l) => l.status === 'SUCCESS').length;
    return Math.round((successes / todayLogs.length) * 100);
  });
  totalTokensToday = computed(() => {
    const today = new Date().toDateString();
    return this.runLogs()
      .filter((l) => new Date(l.firedAt).toDateString() === today)
      .reduce((sum, l) => sum + (l.tokenUsage?.totalTokens ?? 0), 0);
  });

  ngOnInit() {
    if (this.botId) {
      this.loadAll(this.botId);
      this.startPolling();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['botId'] && !changes['botId'].firstChange) {
      const id = changes['botId'].currentValue;
      if (id) {
        this.logsOffset.set(0);
        this.loadAll(id);
        this.startPolling();
      } else {
        this.clearAll();
      }
    }
  }

  ngOnDestroy() {
    this.maybeAutoSaveDelivery();
    this.pollSub?.unsubscribe();
    // Ensure body scrolling is restored if component is destroyed while modal is open
    document.body.style.overflow = '';
  }

  setTab(tab: ViewTab) {
    const prev = this.activeTab();
    if (prev === 'delivery' && tab !== 'delivery') {
      this.maybeAutoSaveDelivery();
    }
    this.activeTab.set(tab);
    if (tab === 'logs' && this.botId) {
      this.logsOffset.set(0);
      this.loadLogs(this.botId);
    }
    if (tab === 'delivery' && this.botId && !this.deliveryConfig()) {
      this.loadDeliveryConfig(this.botId);
    }
  }

  refresh() {
    if (this.botId) this.loadAll(this.botId);
  }

  prevPage() {
    const newOffset = Math.max(0, this.logsOffset() - this.logsPageSize);
    this.logsOffset.set(newOffset);
    if (this.botId) this.loadLogs(this.botId);
  }

  nextPage() {
    if (!this.hasNextPage()) return;
    this.logsOffset.set(this.logsOffset() + this.logsPageSize);
    if (this.botId) this.loadLogs(this.botId);
  }

  onPageChange(page: number) {
    this.logsOffset.set((page - 1) * this.logsPageSize);
    if (this.botId) this.loadLogs(this.botId);
  }

  toggleAgentsExpand(logId: number) {
    this.expandedLogIds.update(ids =>
      ids.includes(logId) ? ids.filter(id => id !== logId) : [...ids, logId]
    );
  }

  toggleActive(schedule: AgentSchedule) {

    this.togglingId.set(schedule.pid);
    this.svc
      .toggleSchedule(schedule.pid, !schedule.isActive)
      .pipe(finalize(() => this.togglingId.set(null)))
      .subscribe({
        next: () => {
          this.schedules.update((list) =>
            list.map((s) => (s.pid === schedule.pid ? { ...s, isActive: !s.isActive } : s))
          );
        },
      });
  }

  deleteSchedule(schedule: AgentSchedule) {
    if (!this.botId) return;
    const ok = window.confirm(`Delete schedule "${schedule.name}"? This is a soft delete and can be recreated from the workflow node.`);
    if (!ok) return;

    this.togglingId.set(schedule.pid);
    this.svc
      .deleteSchedule(this.botId, schedule.pid)
      .pipe(finalize(() => this.togglingId.set(null)))
      .subscribe({
        next: () => {
          this.schedules.update((list) => list.filter((s) => s.pid !== schedule.pid));
        },
      });
  }

  openDagView(log: ScheduleRunLog) {
    this.selectedLogForDag.set(log);
    this.activeTab.set('dag');

    // Optimization: If nested data exists, use it immediately
    if (log.nodeExecutions && log.nodeExecutions.length > 0) {
      this.dagExecutions.set(log.nodeExecutions);
      return;
    }

    // Fallback for old data or edge cases
    if (!log.traceId || !this.botId) {
      this.dagExecutions.set([]);
      return;
    }

    this.selectedExecutionId.set(log.traceId);
    this.loadDag(this.botId, log.traceId);
  }

  openDetailsModal(title: string, content: any, type: 'text' | 'json' = 'text', ev?: Event) {
    this.modalTitle.set(title);
    this.modalContent.set(content);
    this.modalType.set(type);

    if (type === 'json' && content) {
      if (content.outputResponse) {
        this.detailsTab.set('output');
      } else if (content.inputPayload) {
        this.detailsTab.set('input');
      } else if (content.toolResults && content.toolResults.length) {
        this.detailsTab.set('tools');
      }
    }

    this.detailsModalOpen.set(true);
    
    // Ensure the modal appears centered and provide context by centering the clicked row
    if (ev?.currentTarget) {
      (ev.currentTarget as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    // Smooth scroll to top to ensure the fixed modal is fully visible and centered
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    this.detailsModalOpen.set(false);
    document.body.style.overflow = '';
  }

  prettyJson(val: any): string {
    if (!val) return '';
    if (typeof val === 'string') {
      try {
        return JSON.stringify(JSON.parse(val), null, 2);
      } catch {
        return val;
      }
    }
    return JSON.stringify(val, null, 2);
  }

  backToLogs() {
    this.activeTab.set('logs');
    this.dagExecutions.set([]);
    this.selectedExecutionId.set(null);
    this.selectedLogForDag.set(null);
  }

  statusClass(status: string): string {
    switch (status?.toUpperCase()) {
      case 'SUCCESS':  return 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-emerald-200 dark:ring-emerald-500/20';
      case 'FAILED':   return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10 ring-1 ring-red-200 dark:ring-red-500/20';
      case 'RUNNING':  return 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 ring-1 ring-amber-200 dark:ring-amber-500/20';
      case 'SKIPPED':  return 'text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 ring-1 ring-gray-200 dark:ring-gray-600/30';
      case 'RETRYING': return 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-500/10 ring-1 ring-orange-200 dark:ring-orange-500/20';
      default:         return 'text-gray-400 bg-gray-50';
    }
  }

  statusDot(status: string): string {
    switch (status?.toUpperCase()) {
      case 'SUCCESS':  return 'bg-emerald-500';
      case 'FAILED':   return 'bg-red-500';
      case 'RUNNING':  return 'bg-amber-500 animate-pulse';
      case 'SKIPPED':  return 'bg-gray-400';
      case 'RETRYING': return 'bg-orange-500 animate-pulse';
      default:         return 'bg-gray-300';
    }
  }

  formatDuration(ms: number | null): string {
    if (ms == null) return '—';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60_000).toFixed(1)}m`;
  }

  formatTokens(n: number | null | undefined): string {
    if (n == null) return '—';
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return `${n}`;
  }

  getAgentsList(log: ScheduleRunLog): { name: string, toolCount: number }[] {
    const agents: { name: string, toolCount: number }[] = [];
    const seen = new Set<string>();

    const items = (log.nodeExecutions || log.subAgentLogs || []) as any[];
    
    for (const item of items) {
      const name = item.nodeName || item.agentName;
      if (!name || seen.has(name)) continue;

      let toolCount = 0;
      if (item.toolCallCount !== undefined) {
        toolCount = item.toolCallCount;
      } else if (item.toolResults) {
        const results = typeof item.toolResults === 'string' ? JSON.parse(item.toolResults) : item.toolResults;
        toolCount = Array.isArray(results) ? results.length : 0;
      }

      agents.push({ name, toolCount });
      seen.add(name);
    }
    return agents;
  }

  formatTime(iso: string | null): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }

  formatDate(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
      d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  private loadAll(id: number) {
    this.lastFetchedId = id;
    this.loadSchedules(id);
    this.loadLogs(id);
    this.lastRefreshed.set(new Date());
  }

  private loadSchedules(id: number) {
    this.schedulesLoading.set(true);
    this.svc.getSchedules(id)
      .pipe(finalize(() => this.schedulesLoading.set(false)))
      .subscribe({ next: (r) => this.schedules.set(r.data ?? []) });
  }

  private loadLogs(id: number) {
    this.logsLoading.set(true);
    this.svc.getRunLogs(id, this.logsPageSize, this.logsOffset())
      .pipe(finalize(() => this.logsLoading.set(false)))
      .subscribe({
        next: (r) => {
          const payload = r.data;
          this.runLogs.set(payload?.data ?? []);
          this.logsTotal.set(payload?.total ?? 0);
          this.logsPagination.set(payload?.pagination ?? null);
          this.logsTotalTokensUsed.set(payload?.totalTokensUsed ?? 0);
          this.logsPageTokensUsed.set(payload?.pageTokensUsed ?? 0);
        },
      });
  }

  private loadDag(id: number, executionId: string) {
    this.dagLoading.set(true);
    this.svc.getDagExecutions(id, executionId)
      .pipe(finalize(() => this.dagLoading.set(false)))
      .subscribe({ next: (r) => this.dagExecutions.set(r.data ?? []) });
  }

  private startPolling() {
    this.pollSub?.unsubscribe();
    this.pollSub = interval(60_000).subscribe(() => {
      if (this.botId) this.loadAll(this.botId);
    });
  }

  // Template helpers for reduce/filter in @for blocks
  readonly toTokenSum = (acc: number, n: DagNodeExecution) => acc + (n.totalTokens ?? 0);
  readonly toDurationSum = (acc: number, n: DagNodeExecution) => acc + (n.durationMs ?? 0);
  readonly isStaleNode = (n: DagNodeExecution) => n.isStale === true;

  // ── Delivery helpers ───────────────────────────────────────────────────────

  saveDeliveryAll(opts?: { silent?: boolean }) {
    if (!this.botId) return;
    if (this.deliverySaving()) return;

    const enabledEmail = this.emailEnabled();
    const enabledGc = this.googleChatEnabled();
    const recipients = this.normalizeRecipients(this.emailRecipients())
      .split(/,\s*/)
      .map((r) => r.trim())
      .filter(Boolean);
    const webhookUrl = this.normalizeWebhook(this.googleChatWebhookUrl());

    // Validate only when enabling. If invalid and this is an auto-save, do nothing.
    if (enabledEmail && recipients.length === 0) return;
    if (enabledGc && !webhookUrl.startsWith('https://')) return;

    const existing = this.deliveryConfig()?.channels ?? [];
    const existingEmail = existing.find((c) => c.type === 'EMAIL');
    const existingGc = existing.find((c) => c.type === 'GOOGLE_CHAT');

    const emailConfig = enabledEmail
      ? { recipients }
      : { recipients: (existingEmail?.config?.['recipients'] as any) ?? recipients };
    const gcConfig = enabledGc
      ? { webhook_url: webhookUrl }
      : { webhook_url: (existingGc?.config?.['webhook_url'] as any) ?? webhookUrl };

    const channels = [
      ...existing
        .filter((c) => c.type !== 'EMAIL' && c.type !== 'GOOGLE_CHAT')
        .map((c) => ({ type: c.type, config: c.config, enabled: c.enabled })),
      { type: 'EMAIL' as const, config: emailConfig, enabled: enabledEmail },
      { type: 'GOOGLE_CHAT' as const, config: gcConfig, enabled: enabledGc },
    ];

    this.deliverySaving.set(true);
    this.svc
      .saveDeliveryConfig(this.botId, channels)
      .pipe(finalize(() => this.deliverySaving.set(false)))
      .subscribe({ next: (cfg) => this.applyDeliveryConfig(cfg) });
  }

  private maybeAutoSaveDelivery() {
    if (this.activeTab() !== 'delivery') return;
    if (!this.botId) return;
    if (!this.deliveryConfig()) return;
    if (!this.deliveryDirty()) return;
    this.saveDeliveryAll({ silent: true });
  }

  private normalizeRecipients(raw: string): string {
    return raw
      .split(/[,;\n]/)
      .map((r) => r.trim())
      .filter((r) => r.length > 0)
      .join(', ');
  }

  private normalizeWebhook(raw: string): string {
    return (raw ?? '').trim();
  }

  testDelivery() {
    if (!this.botId) return;
    this.deliveryTesting.set(true);
    this.svc
      .testDelivery(this.botId)
      .pipe(finalize(() => this.deliveryTesting.set(false)))
      .subscribe();
  }

  private loadDeliveryConfig(id: number) {
    this.deliveryLoading.set(true);
    this.svc
      .getDeliveryConfig(id)
      .pipe(finalize(() => this.deliveryLoading.set(false)))
      .subscribe({ next: (cfg) => this.applyDeliveryConfig(cfg) });
  }

  private applyDeliveryConfig(cfg: DeliveryConfig) {
    this.deliveryConfig.set(cfg);
    const email = cfg.channels.find((c) => c.type === 'EMAIL');
    const googleChat = cfg.channels.find((c) => c.type === 'GOOGLE_CHAT');

    this.emailEnabled.set(email?.enabled ?? false);
    this.emailRecipients.set(
      Array.isArray(email?.config?.['recipients']) ? (email!.config['recipients'] as string[]).join(', ') : '',
    );

    this.googleChatEnabled.set(googleChat?.enabled ?? false);
    this.googleChatWebhookUrl.set((googleChat?.config?.['webhook_url'] as string | undefined) ?? '');

    this.deliveryBaseline.set({
      emailEnabled: this.emailEnabled(),
      emailRecipients: this.normalizeRecipients(this.emailRecipients()),
      googleChatEnabled: this.googleChatEnabled(),
      googleChatWebhookUrl: this.normalizeWebhook(this.googleChatWebhookUrl()),
    });
  }

  private buildChannelsPayload(
    existing: DeliveryConfig['channels'],
    overrideType: DeliveryChannelType,
    overrideConfig: Record<string, any>,
    enabled: boolean,
  ): { type: DeliveryChannelType; config: Record<string, any>; enabled: boolean }[] {
    const others = existing
      .filter((c) => c.type !== overrideType)
      .map((c) => ({ type: c.type, config: c.config, enabled: c.enabled }));
    return [...others, { type: overrideType, config: overrideConfig, enabled }];
  }

  private clearAll() {
    this.schedules.set([]);
    this.runLogs.set([]);
    this.dagExecutions.set([]);
    this.logsPagination.set(null);
    this.logsTotalTokensUsed.set(0);
    this.logsPageTokensUsed.set(0);
    this.deliveryConfig.set(null);
    this.emailEnabled.set(false);
    this.emailRecipients.set('');
    this.googleChatEnabled.set(false);
    this.googleChatWebhookUrl.set('');
    this.pollSub?.unsubscribe();
  }
}
